import { Injectable } from '@angular/core';

import { IChildRecord, IClassBreakDataResult, IInputType, ILayer, ILayerTableChild, IQuery, IRMSClipRange, ITable, ITableEditableColumn, IValidationType } from '../types';

import { Api } from './api';

@Injectable({
    providedIn: 'root'
})
export class LayerApi {

    constructor(private readonly api: Api) { }

    layerProperties = `
        name,
        id,
        categoryId,
        filter,
        description,
        isGroupLayer,
        queryJSON,
        labelingInfoJSON,
        sources,
        url,
        renderersJSON,
        parentId,
        typeId,
        graphicsJSON,
        routingDetailJSON
    `;

    get Api() { return this.api; }

    async getLayerRecordCount(layerId: number, query?: IQuery) {
        // TODO setup interfaces for layers, features etc
        return await this.api.http2.graphqlRequest<{ maximumRecordUpdateDate: string; recordCount: number }[]>(`
                query ($query: QueryInput!) {
                    layers(id: ${layerId}){
                        recordCount(query: $query)
                        maximumRecordUpdateDate
                    }
                }
        `, { query: query || {} }, 'GET', 'layers');
    }

    /**
     *  Retrieves distinct values for a layer / layer column.
     */
    async getDistinctValues(layerId: number, name: string, query?: IQuery, first: number = 5000) {
        let layers: ILayer[] = null;
        name = name.toUpperCase();
        if (query) {
            layers = await this.api.http2.graphqlRequest<ILayer[]>(`
                    query ($query: QueryInput!) {
                        layers(id:${layerId}) {
                            columns(name:"${name}") 
                                {name distinctValues(query: $query, first:${first})}}}
            `, { layerId, name, query }, 'GET', 'layers');
        } else {
            layers = await this.api.http2.graphqlRequest<ILayer[]>(`
                    query {
                        layers(id:${layerId}) {
                            columns(name:"${name}") 
                                {name distinctValues(first:${first})}}}
            `, { layerId, name, query }, 'GET', 'layers');
        }
        if (layers.length > 0) {
            const columns = layers[0].columns;
            if (columns.length > 0) {
                return columns[0].distinctValues.sort();
            }
        }
        return [];
    }

    /**
     *  Retrieves distinct values for a table column.
     */
    async getColumnDistinctValues(tableName: string, tableOwner: string, columnName: string, query?: IQuery, first: number = 5000) {
        const tables = await this.api.http2.graphqlRequest<ITable[]>(`
            query ($query: QueryInput, $tableName: [String], $tableOwner: [String], $columnName: [String], $first: Int) {
                tables(name: $tableName, owner: $tableOwner) {
                    columns(name: $columnName) {
                        distinctValues(first: $first, query: $query)
                    }
                }
            }
            `, { tableName, tableOwner, columnName, first, query }, 'GET', 'tables');
        let distinct: string[] = [];
        if (tables.length > 0) {
            const columns = tables[0].columns;
            if (columns.length > 0) {
                distinct = columns[0].distinctValues.sort();
            }
        }
        return distinct;
    }

    /**
     *  Retrieve map layers.
     *  @param id ID of layer to be retrieved. Optional - leave blank [] to get all.
     *  @param query Specify layer properties if you want to return data other than default. Optional
     */
    async getLayers(id?: number[], props?: string) {
        props = props ? props : this.layerProperties;
        // TODO setup interfaces for layers, features etc
        return await this.api.http2.graphqlRequest<ILayer[]>(`
                query GetLayers($id: [Int]) {
                    layers(id: $id){
                        ${props}
                    }
                }
        `, { id }, 'GET', 'layers');
    }

    /**
     *  Retrieves layer data for the grid.
     */
    async getLayerInfo(layerId: number) {
        return await this.getLayers([layerId],
            'id name queryJSON columns(orderBy:["alias","name"]){alias, name, databaseDataType, databaseDataLength, databaseColumnOrder, columnIdentifierId}');
    }

    async getData(layerId: number, fieldNames: string[], query?: IQuery, first: number = 1000) {
        let layers = null;
        const fieldNamesJoined = fieldNames.map(x => `"${x}"`).join(',');
        if (query) {
            layers = await this.api.http2.graphqlRequest(`
                    query ($query: QueryInput!) {
                        layers(id:${layerId}) {
                            data(query: $query, fieldNames:[${fieldNamesJoined}], first:${first})                        
                        }}
            `, { layerId, query }, 'GET', 'layers');
        } else {
            layers = await this.api.http2.graphqlRequest(`
                    query {
                        layers(id:${layerId}) {
                            data(fieldNames:[${fieldNamesJoined}], first:${first})                        
                        }}
            `, { layerId }, 'GET', 'layers');
        }
        if (layers.length > 0) {
            const data = layers[0].data;
            if (data.length > 0) {
                return data;
            }
        }
        return [];
    }

    async getClassBreakData(layerId: number, fieldNames: string[], classes: number, forceUniqueValues: boolean, query?: IQuery, columnIsDate?: boolean, classBreakLabels?: string) {
        const fieldNamesJoined = fieldNames.map(x => `"${x}"`).join(',');
        return await this.api.http2.graphqlRequest<IClassBreakDataResult[]>(`
                query ($query: QueryInput!) {
                    layers(id:${layerId}) {
                        classBreakData(${query ? 'query: $query, ' : ''}fieldNames:[${fieldNamesJoined}], classes: ${classes}, forceUnique: ${forceUniqueValues}, columnIsDate: ${columnIsDate}, useNewLabels: true, classBreakLabels: ${classBreakLabels})
                    }}
        `, { layerId, query }, 'GET', 'layers');
    }

    /**
     * Text based layer search
     * @param layerSearchText Input string to search layer catalog
     */
    async getLayerSearch(layerSearchText: string) {
        // TODO setup interfaces for layers, features etc
        return await this.api.http2.graphqlRequest(`
                query GetLayers($layerSearchText: [String]) {
                    layers(search: $layerSearchText){
                        ${this.layerProperties}
                    }
                }
        `, { layerSearchText }, 'GET', 'layers');
    }

    /**
     *  Retrieves distinct values for a table / table column.
     */
    async getDistinctTableValues(tableId: number, name: string, first: number = 5000) {
        name = name.toUpperCase();
        const table = await this.api.http2.graphqlRequest<ITable[]>(`
                query {
                    tables(id:${tableId}) {
                        columns(name:"${name}") 
                            {owner name distinctValues(first:${first})}}}
        `, { tableId, name }, 'GET', 'tables');
        const columns = table[0].columns;
        if (columns.length > 0) {
            return columns[0].distinctValues.sort();
        }
        return [];
    }

    async getLayerChildTables(layerId: number) {
        return await this.api.http2.graphqlRequest<ILayerTableChild[]>(`
          query GetLayerChildTables($layerId: [Int]) {
              layerChildTables(layerId: $layerId) {
                  layerId
                  tableId
                  parentPrimaryKeyColumnName
                  childParentKeyColumnName
                  layerChildRelationshipName
                  orderKeyColumnName
                  columnList
              }
          }
        `, { layerId }, 'GET', 'layerChildTables');
    }

    async getAllLayerChildTables() {
        return await this.api.http2.graphqlRequest<ILayerTableChild[]>(`
          query GetLayerChildTables {
              layerChildTables{
                  layerId
                  tableId
                  parentPrimaryKeyColumnName
                  childParentKeyColumnName
                  layerChildRelationshipName
                  orderKeyColumnName
              }
          }
        `, {}, 'GET', 'layerChildTables');
    }

    async getLayerChildTable(layerId: number, tableId: number | number[], props: string) {
        return await this.api.http2.graphqlRequest(`
          query GetLayerChildTable($layerId: [Int], $tableId: [Int]) {
              layers(id: $layerId) {
                  childTables(id: $tableId) {
                      ${props}
                  }
              }
          }
        `, { layerId, tableId }, 'GET', 'layers');
    }

    async getLayerChildTableWithData(layerId: number, tableId: number | number[], names: string[], query: IQuery) {
        const fieldNames = names.join(',');
        return await this.api.http2.graphqlRequest(`
          query GetLayerChildTable($layerId: [Int], $tableId: [Int], $fieldNames: [String]!, $query:QueryInput) {
              layers(id: $layerId) {
                  childTables(id: $tableId) {
                     id
                     data(
                     fieldNames: $fieldNames,
                     query: $query)
                  }
              }
          }
        `, { layerId, tableId, fieldNames, query }, 'GET', 'layers');
    }

    async getTableRMSKeysByName(tableName: string) {
        return await this.api.http2.graphqlRequest(`
            query GetTableRMSByName($tableName: [String]) {
              getTableRMSByName(tableName: $tableName) {
                id
                tableId
                layerDisplayName
                tableDisplayName
                tableName
                geometryType
                ctyCodeColName
                strtnoColName
                jurisColName
                segBgnColName
                offsetBgnColName
                segEndColName
                offsetEndColName
                sideIndColName
              }
            }`, { tableName }, 'GET', 'getTableRMSByName');
    }

    async getTableRMSKeys() {
        return await this.api.http2.graphqlRequest(`
            query GetLayerTableRoadway {
              layerTableRoadway {
                id
                tableId
                layerDisplayName
                tableDisplayName
                tableName
                geometryType
                ctyCodeColName
                strtnoColName
                jurisColName
                segBgnColName
                offsetBgnColName
                segEndColName
                offsetEndColName
                sideIndColName
              }
            }`, {}, 'GET', 'layerTableRoadway');
    }

    async getTable(tableId: number[] | number, props: string) {
        return await this.api.http2.graphqlRequest<ITable[]>(`
          query GetTable($tableId: [Int]) {
              tables(id: $tableId) {
                  ${props}
              }
          }
        `, { tableId }, 'GET', 'tables');
    }

    /**
     *  Retrieve layer/table information for the nearby feature for this application
     *  @param appId Application ID to return nearbyAssets for.
     */
    async getApplicationNearbyAssets(appId: number) {
        return await this.api.http2.graphqlRequest(`
            query {
                applications(id: ${appId}) {
                    nearbyAssets {
                        id,
                        parentId,
                        relationshipName,
                        layerId,
                        tableId,
                        parentJoinKey,
                        joinKey,
                        columnList
                        templates {
                            id
                            sourceId
                            sourceType
                            type
                            functionType
                            format
                            layout
                        }
                    }
                }
            }
        `, {}, 'GET', 'applications');
    }

    /**
     *  Retrieve children of the passed in layerTableChildId from the layerTable hierarchy
     *  @param parentId ID(s) of parent record in heirarchy to return children for.
     */
    async getLayerTableChildren(parentId: number | number[]) {
        return await this.api.http2.graphqlRequest<number>(`
                query ($parentId: [Int]) {
                    layerTableChildren(parentId: $parentId){
                        id
                        parentId
                        relationshipName
                        layerId
                        tableId
                        parentJoinKey
                        joinKey
                        columnList
                    }
                }
        `, {parentId}, 'GET', 'layerTableChildren');
    }

    async shareLayer(userIds: number[], layerId: number) {
        // TODO setup interfaces for layers, features etc
        return await this.api.http2.graphqlRequest(`
                mutation ShareLayer($userIds: [Int], $layerId: Int) {
                    shareLayer(userIds: $userIds, layerId: $layerId)
                }
        `, { userIds, layerId }, 'GET', 'shareLayer');
    }

    async unshareLayer(userId: number[], layerId: number) {
        // TODO setup interfaces for layers, features etc
        return await this.api.http2.graphqlRequest(`
                mutation UnshareLayer($userId: [Int], $layerId: Int) {
                    unshareLayer(userIds: $userId, layerId: $layerId)
                }
        `, { userId, layerId }, 'GET', 'unshareLayer');
    }
    async saveLayer(layer, userId: number) {
        const properties = Object.keys(layer);
        if (properties.indexOf('id') < 0) {
            properties.push('id');
        }

        const propertiesCommaDelimited = properties.join(',');
        return await this.api.http2.graphqlRequest(`
            mutation saveLayer($layer: LayerInput!, $userId: Int){
                saveLayer(layer: $layer, userId: $userId){
                    ${propertiesCommaDelimited}
                }
            }
        `, { layer: this.sanitize(layer), userId }, 'POST', 'saveLayer.id');
    }

    async createClippedRMSGeom(clipRanges: IRMSClipRange | IRMSClipRange[]) {
        const ranges: IRMSClipRange[] = Array.isArray(clipRanges) ? clipRanges : [clipRanges];
        let request = 'mutation createMultipleClippedRMSGeom { createMultipleClippedRMSGeom( rmsobject: [';
        for (const r of ranges) {
            request += `{county: "${r.county}", route: "${r.route}", seg1: "${r.seg1}", offset1: "${r.offset1}", seg2: "${r.seg2}", offset2: "${r.offset2}", side: "${r.side}"}`;
        }
        request += '])}';
        return await this.api.http2.graphqlRequest(request, null, 'POST', 'createMultipleClippedRMSGeom');
    }

    sanitize(layer) {
        //only keep layer properties
        const result = this.api.pick(layer, this.layerProperties.split(',').map(p => p.trim()));
        if (!result.id) {
            delete layer.id;
        }
        return result;
    }

    async delete(layerId: number) {
        return await this.api.http2.graphqlRequest(`
            mutation DeleteLayer($layerId: Int!) {
                deleteLayer(id: $layerId)
            }
        `, { layerId });
    }

    async getDocSources(layerId: number[] | number): Promise<any[] | null> {
        const res = await this.api.http2.graphqlRequest(`
          query GetLayerDocSources($layerId: [Int]) {
            layers(id: $layerId) {
              name
              layerDocSource {
                id
                fields
                type
                template
                options
                url
              }
            }
          }`, { layerId });
        if (!res || !res.layers || res.layers.length !== 1 || !res.layers[0].layerDocSource) {
            return null;
        }
        return res.layers[0].layerDocSource;

    }

    /**
     * Child Records Filter
     * @param childRecordsFilter Get child tables by layer
     */
    async getLayerChildRelationships(parentLayerId: number, props: string) {
        const res = await this.api.http2.graphqlRequest<IChildRecord[]>(`
            query GetLayerChildRelationships($parentLayerId:[Int]) {
              layerChildRelationships(parentLayerId:$parentLayerId){
                ${props}
              }
            }`, { parentLayerId }, 'GET', 'layerChildRelationships');
        return res;
    }

    async getChildRecord(layerId: number): Promise<any[] | null> {
        const res = await this.api.http2.graphqlRequest(`
            query GetLayerChildRelationships($parentLayerId:[Int]) {
              layerChildRelationships(parentLayerId:$parentLayerId){
                layerChildRelationshipName
	            id
              }
            }`, { layerId });
        if (!res || !res.layerChildRelationships || res.layerChildRelationships.length !== 1) {
            return null;
        }
        return res.layers;
    }

    async getUserLayersFolders(id: number, properties: string) {
        return await this.api.http2.graphqlRequest(`query getUserLayersFolders($id: [Int]) {
                users(id: $id) {
                    ${properties}
                }
            }`, { id }, 'GET', 'users');
    }

    async saveLayerToUserFolder(layerId, folderId) {
        return await this.api.http2.graphqlRequest(`
            mutation SaveLayerToUserFolder($layerId: Int, $folderId: Int) {
                saveLayerToUserFolder(newUserFolderId: $folderId, layerId: $layerId)
            }
        `, { layerId, folderId }, 'POST', 'saveLayerToUserFolder');
    }

    async getUserLayers(layerId: number) {
        return await this.api.http2.graphqlRequest(
            `query GetUserLayers($layerId: [Int]) {
                    layers(id: $layerId) {
                        userLayers{
                            accessLevel
                            userId
                        }
                    }
            } `, { layerId }, 'GET', 'layers');
    }

    async emailAboutSharedLayer(emailAddresses: string[], emailSubject: string, emailBody: string) {
        return await this.api.http2.graphqlRequest(`
            mutation EmailAboutSharedLayer($emailAddresses: [String], $emailSubject: String, $emailBody: String) {
                emailAboutSharedLayer(emailAddresses: $emailAddresses, emailSubject: $emailSubject, emailBody: $emailBody)
            }
        `, {emailAddresses, emailSubject, emailBody}, 'GET', 'emailAboutSharedLayer');
    }

    async getTableEditableColumns(tableId: number) {
        return (await this.api.http2.graphqlRequest<
            {
                id: number;
                name: string;
                displayOrder: number;
                inputType?: string;
                isEditable: string;
                isInsertable: string;
                validationType?: string;
                tableId: number;
            }[]>(`
            query GetTableEditableColumns($tableId: [Int]) {
                tableEditableColumns(tableId: $tableId) {
                    id
                    name
                    displayOrder
                    inputType
                    isEditable
                    isInsertable
                    validationType
                    tableId
                }
            }
        `, { tableId }, 'GET', 'tableEditableColumns')).map(tableEditableColumn => ({
            id: tableEditableColumn.id,
            name: tableEditableColumn.name,
            displayOrder: tableEditableColumn.displayOrder,
            tableId: tableEditableColumn.tableId,
            isEditable: tableEditableColumn.isEditable.toLowerCase() === 't',
            isInsertable: tableEditableColumn.isInsertable.toLowerCase() === 't',
            inputType: tableEditableColumn.inputType ? JSON.parse(tableEditableColumn.inputType) as IInputType : undefined,
            validationType: tableEditableColumn.validationType ? JSON.parse(tableEditableColumn.validationType) as IValidationType : undefined
        } as ITableEditableColumn));
    }

    async getPetlProjectColumnAliases(projectId: number) {
        return await this.api.http2.request<{ columnName: string; aliasName: string; isUsed: boolean }[]>(`petl/projects/${projectId}/column-aliases`, { method: 'GET' });
    }

    async getBaseParentId(parentId: number) {
        return await this.api.http2.graphqlRequest<number>(`
          query GetBaseParentId($parentId: Int!) {
              baseParentId(parentId: $parentId)
          }
        `, { parentId }, 'GET', 'baseParentId');
    }

    async getLayerPermissions(layerIds: number[]) {
        return await this.api.http2.graphqlRequest<{ layerId: number; name: string }[]>(`
            query GetLayerPermissions($layerId: [Int]) {
                layerPermissions(layerId: $layerId) { layerId, name }
            }
        `, { layerId: layerIds }, 'GET', 'layerPermissions');
    }

    async getFilterDateFormats() {
        return await this.api.http2.graphqlRequest(`
            query GetFilterDateFormats {
                filterDateFormats
            }
        `, {}, 'GET', 'filterDateFormats');
    }
}
